import { useEffect, useState } from "react"
import { Link, useParams } from "react-router-dom"
import {
  Badge,
  Button,
  Card,
  ErrorNote,
  Field,
  Modal,
  Select,
  Skeleton,
  formatDate,
  fromDateInput,
  toDateInput,
  useLoad,
  useToast,
} from "../components/ui"
import { api, type BoardConfig, type BoardColor, type Sprint, type StatusCategory } from "../lib/api"
import { arrayMove } from "../lib/arrayMove"
import { COLOR_DOT } from "../lib/boardConfig"

// The board's configuration: statuses, types, priorities, labels, custom fields.
//
// Everything edits a local draft and one Save sends the whole thing — the PUT
// validates as a unit (per-category minimums, in-use deletions), so saving
// field-by-field would let the screen reach states the server must refuse.
//
// Admin-gated server-side; this page just shows the 403 to anyone else.

const COLORS: BoardColor[] = ["gray", "blue", "cyan", "green", "yellow", "orange", "red", "purple", "pink"]
const CATEGORIES: { value: StatusCategory; label: string }[] = [
  { value: "todo", label: "To do" },
  { value: "in_progress", label: "In progress" },
  { value: "done", label: "Done" },
]

type Usage = {
  statuses: Record<string, number>
  types: Record<string, number>
  priorities?: Record<string, number>
  fields: Record<string, number>
}

export function ProjectSettingsPage() {
  const { projectId = "" } = useParams()
  const toast = useToast()
  const loaded = useLoad(
    () => api.get<{ config: BoardConfig; usage: Usage }>(`/projects/${projectId}/settings`),
    [projectId],
  )

  const [draft, setDraft] = useState<BoardConfig | null>(null)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<unknown>(null)
  useEffect(() => {
    if (loaded.data) setDraft(structuredClone(loaded.data.config))
  }, [loaded.data])

  if (loaded.loading || !draft) {
    return loaded.error ? <ErrorNote error={loaded.error} /> : <Skeleton className="h-64" />
  }
  const usage = loaded.data!.usage

  const dirty = JSON.stringify(draft) !== JSON.stringify(loaded.data!.config)

  async function save() {
    setSaving(true)
    setError(null)
    try {
      await api.put(`/projects/${projectId}/settings`, draft)
      toast("Board settings saved")
      loaded.reload()
    } catch (err) {
      setError(err)
    } finally {
      setSaving(false)
    }
  }

  return (
    <>
      <header className="flex flex-wrap items-end justify-between gap-3 border-b border-border pb-4">
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-2 text-xs text-faint">
            <Link to="/projects" className="hover:text-muted hover:underline">Projects</Link>
            <span>/</span>
            <Link to={`/projects/${projectId}`} className="hover:text-muted hover:underline">Board</Link>
          </div>
          <h1 className="text-2xl font-semibold tracking-tight text-foreground">Board settings</h1>
          <p className="text-sm text-muted">
            Statuses, types, priorities, labels and fields for this project only. Renames apply everywhere at once.
          </p>
        </div>
        <div className="flex gap-2">
          {dirty && (
            <Button variant="subtle" onClick={() => setDraft(structuredClone(loaded.data!.config))} disabled={saving}>
              Discard
            </Button>
          )}
          <Button onClick={() => void save()} disabled={!dirty || saving}>
            {saving ? "Saving…" : "Save changes"}
          </Button>
        </div>
      </header>

      <ErrorNote error={error} />

      <div className="mt-4 grid gap-6 lg:grid-cols-2">
        <StatusesCard draft={draft} setDraft={setDraft} usage={usage} />
        <TypesCard draft={draft} setDraft={setDraft} usage={usage} />
        <PrioritiesCard draft={draft} setDraft={setDraft} usage={usage} />
        <LabelsCard draft={draft} setDraft={setDraft} />
        <FieldsCard draft={draft} setDraft={setDraft} usage={usage} />
      </div>

      <div className="mt-6">
        <SprintsCard projectId={projectId} />
      </div>
    </>
  )
}

type DraftProps = {
  draft: BoardConfig
  setDraft: React.Dispatch<React.SetStateAction<BoardConfig | null>>
}

function ColorPicker({ value, onChange }: { value: BoardColor; onChange: (c: BoardColor) => void }) {
  return (
    <div className="flex gap-1">
      {COLORS.map((color) => (
        <button
          key={color}
          type="button"
          aria-label={color}
          title={color}
          onClick={() => onChange(color)}
          className={`size-4 rounded-full ${COLOR_DOT[color]} ${value === color ? "ring-2 ring-interactive ring-offset-1 ring-offset-surface" : "opacity-50 hover:opacity-100"}`}
        />
      ))}
    </div>
  )
}

function RowShell({ children }: { children: React.ReactNode }) {
  return <div className="flex flex-wrap items-center gap-2 rounded border border-border px-2 py-1.5">{children}</div>
}

/** Where a dragged status would land, drawn in the gap between two rows. */
function DropLine({ at }: { at: "top" | "bottom" }) {
  return (
    <div
      aria-hidden="true"
      className={`absolute inset-x-0 h-0.5 rounded-full bg-interactive ${at === "top" ? "-top-[7px]" : "-bottom-[7px]"}`}
    />
  )
}

function InUse({ count, what }: { count: number; what: string }) {
  return count > 0 ? (
    <span className="text-[11px] text-faint" title={`${count} ${what} — move or clear them to delete this`}>
      {count} {what}
    </span>
  ) : null
}

function StatusesCard({ draft, setDraft, usage }: DraftProps & { usage: Usage }) {
  const [name, setName] = useState("")
  const [category, setCategory] = useState<StatusCategory>("in_progress")

  // Reordering uses native drag, like the board's cards, plus arrow buttons for
  // the touch and keyboard users HTML5 drag never reaches. The row is draggable
  // only while its handle is held, so selecting text in the name input still works.
  const [armed, setArmed] = useState<number | null>(null)
  const [dragging, setDragging] = useState<number | null>(null)
  // The gap the dragged row would land in: 0 is above the first row, length below the last.
  const [dropAt, setDropAt] = useState<number | null>(null)

  const update = (i: number, patch: Partial<BoardConfig["statuses"][number]>) =>
    setDraft((d) => d && { ...d, statuses: d.statuses.map((s, j) => (j === i ? { ...s, ...patch } : s)) })
  const move = (from: number, to: number) =>
    setDraft((d) => d && { ...d, statuses: arrayMove(d.statuses, from, to) })
  const endDrag = () => {
    setArmed(null)
    setDragging(null)
    setDropAt(null)
  }
  // Dropping into the gap either side of the dragged row changes nothing, so it draws no line.
  const showsLine = (gap: number) => dragging !== null && dropAt === gap && gap !== dragging && gap !== dragging + 1
  const last = draft.statuses.length - 1

  return (
    <Card className="flex flex-col gap-3 p-4">
      <div>
        <h2 className="text-sm font-semibold text-foreground">Statuses</h2>
        <p className="text-xs text-muted">
          The board's columns, in this order — drag or use the arrows to reorder. The category is
          fixed once created — it is what charts and "overdue" mean by a status.
        </p>
      </div>

      {draft.statuses.map((status, i) => (
        <div
          key={status.id || `new-${i}`}
          draggable={armed === i}
          onDragStart={(event) => {
            event.dataTransfer.setData("text/x-status-index", String(i))
            event.dataTransfer.effectAllowed = "move"
            setDragging(i)
          }}
          onDragOver={(event) => {
            if (dragging === null) return
            event.preventDefault()
            event.dataTransfer.dropEffect = "move"
            const box = event.currentTarget.getBoundingClientRect()
            setDropAt(event.clientY > box.top + box.height / 2 ? i + 1 : i)
          }}
          onDrop={(event) => {
            event.preventDefault()
            if (dragging !== null && dropAt !== null) move(dragging, dropAt > dragging ? dropAt - 1 : dropAt)
            endDrag()
          }}
          onDragEnd={endDrag}
          className={`relative ${dragging === i ? "opacity-40" : ""}`}
        >
          {showsLine(i) && <DropLine at="top" />}
          {i === last && showsLine(i + 1) && <DropLine at="bottom" />}
          <RowShell>
            <span
              aria-hidden="true"
              title="Drag to reorder"
              onMouseDown={() => setArmed(i)}
              onMouseUp={() => setArmed(null)}
              className="cursor-grab select-none px-0.5 text-faint hover:text-foreground active:cursor-grabbing"
            >
              ⋮⋮
            </span>
            <span className="flex flex-col">
              <button
                type="button"
                aria-label={`Move ${status.name} up`}
                disabled={i === 0}
                className="text-[10px] leading-none text-faint hover:text-foreground disabled:opacity-30"
                onClick={() => move(i, i - 1)}
              >
                ▲
              </button>
              <button
                type="button"
                aria-label={`Move ${status.name} down`}
                disabled={i === last}
                className="text-[10px] leading-none text-faint hover:text-foreground disabled:opacity-30"
                onClick={() => move(i, i + 1)}
              >
                ▼
              </button>
            </span>
            <input
              value={status.name}
              aria-label="Status name"
              onChange={(e) => update(i, { name: e.target.value })}
              className="w-32 rounded border border-transparent bg-transparent px-1.5 py-0.5 text-sm text-foreground outline-none hover:border-border focus:border-interactive"
            />
            <span className="text-[11px] text-faint">
              {CATEGORIES.find((c) => c.value === status.category)?.label}
            </span>
            <ColorPicker value={status.color} onChange={(color) => update(i, { color })} />
            <span className="ml-auto flex items-center gap-2">
              <InUse count={usage.statuses[status.id] ?? 0} what="tasks" />
              <button
                type="button"
                className="text-xs text-faint hover:text-danger disabled:cursor-not-allowed disabled:opacity-40"
                disabled={(usage.statuses[status.id] ?? 0) > 0}
                title={
                  (usage.statuses[status.id] ?? 0) > 0
                    ? "Move its tasks first"
                    : "Delete this status"
                }
                onClick={() => setDraft((d) => d && { ...d, statuses: d.statuses.filter((_, j) => j !== i) })}
              >
                remove
              </button>
            </span>
          </RowShell>
        </div>
      ))}

      <div className="flex flex-wrap items-end gap-2 border-t border-border pt-3">
        <Field label="New status" value={name} onChange={(e) => setName(e.target.value)} placeholder="QA" />
        <Select label="Category" value={category} onChange={(e) => setCategory(e.target.value as StatusCategory)}>
          {CATEGORIES.map((c) => (
            <option key={c.value} value={c.value}>{c.label}</option>
          ))}
        </Select>
        <Button
          variant="subtle"
          disabled={!name.trim()}
          onClick={() => {
            setDraft((d) => d && {
              ...d,
              statuses: [...d.statuses, { id: "", name: name.trim(), category, color: "gray" }],
            })
            setName("")
          }}
        >
          Add
        </Button>
      </div>
    </Card>
  )
}

function TypesCard({ draft, setDraft, usage }: DraftProps & { usage: Usage }) {
  const [name, setName] = useState("")
  const update = (i: number, patch: Partial<BoardConfig["types"][number]>) =>
    setDraft((d) => d && { ...d, types: d.types.map((t, j) => (j === i ? { ...t, ...patch } : t)) })

  return (
    <Card className="flex flex-col gap-3 p-4">
      <div>
        <h2 className="text-sm font-semibold text-foreground">Task types</h2>
        <p className="text-xs text-muted">What kind of work a task is — feature, bug, or your own.</p>
      </div>

      {draft.types.map((type, i) => (
        <RowShell key={type.id}>
          <input
            value={type.name}
            aria-label="Type name"
            onChange={(e) => update(i, { name: e.target.value })}
            className="w-32 rounded border border-transparent bg-transparent px-1.5 py-0.5 text-sm text-foreground outline-none hover:border-border focus:border-interactive"
          />
          <ColorPicker value={type.color} onChange={(color) => update(i, { color })} />
          <span className="ml-auto flex items-center gap-2">
            <InUse count={usage.types[type.id] ?? 0} what="tasks" />
            <button
              type="button"
              className="text-xs text-faint hover:text-danger disabled:cursor-not-allowed disabled:opacity-40"
              disabled={(usage.types[type.id] ?? 0) > 0}
              onClick={() => setDraft((d) => d && { ...d, types: d.types.filter((_, j) => j !== i) })}
            >
              remove
            </button>
          </span>
        </RowShell>
      ))}

      <div className="flex items-end gap-2 border-t border-border pt-3">
        <Field label="New type" value={name} onChange={(e) => setName(e.target.value)} placeholder="Spike" />
        <Button
          variant="subtle"
          disabled={!name.trim()}
          onClick={() => {
            setDraft((d) => d && { ...d, types: [...d.types, { id: "", name: name.trim(), color: "gray" }] })
            setName("")
          }}
        >
          Add
        </Button>
      </div>
    </Card>
  )
}

function PrioritiesCard({ draft, setDraft, usage }: DraftProps & { usage: Usage }) {
  const [name, setName] = useState("")
  const priorities = draft.priorities ?? []
  const used = (id: string) => usage.priorities?.[id] ?? 0
  const update = (i: number, patch: Partial<BoardConfig["priorities"][number]>) =>
    setDraft((d) => d && { ...d, priorities: d.priorities.map((p, j) => (j === i ? { ...p, ...patch } : p)) })
  // Order is rank — most urgent first — so moving a row is how an admin says
  // one priority outranks another.
  const move = (i: number, by: -1 | 1) =>
    setDraft((d) => {
      if (!d) return d
      const next = [...d.priorities]
      const j = i + by
      if (j < 0 || j >= next.length) return d
      ;[next[i], next[j]] = [next[j]!, next[i]!]
      return { ...d, priorities: next }
    })

  return (
    <Card className="flex flex-col gap-3 p-4">
      <div>
        <h2 className="text-sm font-semibold text-foreground">Priorities</h2>
        <p className="text-xs text-muted">
          Most urgent first. The default is what a new task gets when nobody picks one.
        </p>
      </div>

      {priorities.map((priority, i) => (
        <RowShell key={priority.id || `new-${i}`}>
          <span className="flex flex-col">
            <button
              type="button"
              aria-label={`Move ${priority.name} up`}
              disabled={i === 0}
              className="text-[10px] leading-none text-faint hover:text-foreground disabled:opacity-30"
              onClick={() => move(i, -1)}
            >
              ▲
            </button>
            <button
              type="button"
              aria-label={`Move ${priority.name} down`}
              disabled={i === priorities.length - 1}
              className="text-[10px] leading-none text-faint hover:text-foreground disabled:opacity-30"
              onClick={() => move(i, 1)}
            >
              ▼
            </button>
          </span>
          <input
            value={priority.name}
            aria-label="Priority name"
            onChange={(e) => update(i, { name: e.target.value })}
            className="w-32 rounded border border-transparent bg-transparent px-1.5 py-0.5 text-sm text-foreground outline-none hover:border-border focus:border-interactive"
          />
          <ColorPicker value={priority.color} onChange={(color) => update(i, { color })} />
          <label
            className="flex items-center gap-1 text-[11px] text-faint"
            title={priority.id ? "New tasks get this priority" : "Save first to make it the default"}
          >
            <input
              type="radio"
              name="default-priority"
              disabled={!priority.id}
              checked={draft.defaultPriority === priority.id}
              onChange={() => setDraft((d) => d && { ...d, defaultPriority: priority.id })}
            />
            default
          </label>
          <span className="ml-auto flex items-center gap-2">
            <InUse count={used(priority.id)} what="tasks" />
            <button
              type="button"
              className="text-xs text-faint hover:text-danger disabled:cursor-not-allowed disabled:opacity-40"
              disabled={used(priority.id) > 0 || draft.defaultPriority === priority.id || priorities.length <= 1}
              title={
                used(priority.id) > 0
                  ? "Move its tasks first"
                  : draft.defaultPriority === priority.id
                    ? "Choose another default first"
                    : "Delete this priority"
              }
              onClick={() => setDraft((d) => d && { ...d, priorities: d.priorities.filter((_, j) => j !== i) })}
            >
              remove
            </button>
          </span>
        </RowShell>
      ))}

      <div className="flex items-end gap-2 border-t border-border pt-3">
        <Field label="New priority" value={name} onChange={(e) => setName(e.target.value)} placeholder="Show-stopper" />
        <Button
          variant="subtle"
          disabled={!name.trim()}
          onClick={() => {
            setDraft((d) => d && { ...d, priorities: [...(d.priorities ?? []), { id: "", name: name.trim(), color: "gray" }] })
            setName("")
          }}
        >
          Add
        </Button>
      </div>
    </Card>
  )
}

function LabelsCard({ draft, setDraft }: DraftProps) {
  const [name, setName] = useState("")

  return (
    <Card className="flex flex-col gap-3 p-4">
      <div>
        <h2 className="text-sm font-semibold text-foreground">Labels</h2>
        <p className="text-xs text-muted">
          A curated list offered when labelling tasks. Free-typed labels keep working; this is the
          menu, not a fence.
        </p>
      </div>

      <div className="flex flex-wrap gap-1.5">
        {draft.labels.length === 0 && <span className="text-xs text-faint">None yet.</span>}
        {draft.labels.map((label, i) => (
          <span key={label.name} className="flex items-center gap-1.5 rounded-full border border-border px-2 py-0.5 text-xs text-foreground">
            <span className={`size-2 rounded-full ${COLOR_DOT[label.color]}`} />
            {label.name}
            <button
              type="button"
              aria-label={`Remove ${label.name}`}
              className="text-faint hover:text-danger"
              onClick={() => setDraft((d) => d && { ...d, labels: d.labels.filter((_, j) => j !== i) })}
            >
              ×
            </button>
          </span>
        ))}
      </div>

      <div className="flex items-end gap-2 border-t border-border pt-3">
        <Field label="New label" value={name} onChange={(e) => setName(e.target.value)} placeholder="regression" />
        <Button
          variant="subtle"
          disabled={!name.trim()}
          onClick={() => {
            const clean = name.trim()
            setDraft((d) => {
              if (!d || d.labels.some((l) => l.name.toLowerCase() === clean.toLowerCase())) return d
              return { ...d, labels: [...d.labels, { name: clean, color: COLORS[(d.labels.length + 1) % COLORS.length]! }] }
            })
            setName("")
          }}
        >
          Add
        </Button>
      </div>
    </Card>
  )
}

function FieldsCard({ draft, setDraft, usage }: DraftProps & { usage: Usage }) {
  const [name, setName] = useState("")
  const [type, setType] = useState<"text" | "number" | "date" | "select">("text")
  const [options, setOptions] = useState("")

  return (
    <Card className="flex flex-col gap-3 p-4">
      <div>
        <h2 className="text-sm font-semibold text-foreground">Custom fields</h2>
        <p className="text-xs text-muted">
          Extra properties on every task's sidebar, and columns in the CSV export. The type is
          fixed once created.
        </p>
      </div>

      {draft.fields.map((field, i) => (
        <RowShell key={field.id}>
          <span className="text-sm text-foreground">{field.name}</span>
          <span className="rounded bg-surface-muted px-1.5 py-0.5 font-mono text-[11px] text-muted">{field.type}</span>
          {field.type === "select" && (
            <span className="truncate text-[11px] text-faint">{field.options?.join(" · ")}</span>
          )}
          <span className="ml-auto flex items-center gap-2">
            <InUse count={usage.fields[field.id] ?? 0} what="values" />
            <button
              type="button"
              className="text-xs text-faint hover:text-danger disabled:cursor-not-allowed disabled:opacity-40"
              disabled={(usage.fields[field.id] ?? 0) > 0}
              onClick={() => setDraft((d) => d && { ...d, fields: d.fields.filter((_, j) => j !== i) })}
            >
              remove
            </button>
          </span>
        </RowShell>
      ))}

      <div className="flex flex-wrap items-end gap-2 border-t border-border pt-3">
        <Field label="New field" value={name} onChange={(e) => setName(e.target.value)} placeholder="Environment" />
        <Select label="Type" value={type} onChange={(e) => setType(e.target.value as typeof type)}>
          <option value="text">text</option>
          <option value="number">number</option>
          <option value="date">date</option>
          <option value="select">select</option>
        </Select>
        {type === "select" && (
          <Field
            label="Options (comma-separated)"
            value={options}
            onChange={(e) => setOptions(e.target.value)}
            placeholder="prod, staging"
          />
        )}
        <Button
          variant="subtle"
          disabled={!name.trim() || (type === "select" && !options.trim())}
          onClick={() => {
            setDraft((d) => d && {
              ...d,
              fields: [
                ...d.fields,
                {
                  id: "",
                  name: name.trim(),
                  type,
                  ...(type === "select"
                    ? { options: options.split(",").map((o) => o.trim()).filter(Boolean) }
                    : {}),
                },
              ],
            })
            setName("")
            setOptions("")
          }}
        >
          Add
        </Button>
      </div>
    </Card>
  )
}

// ---------- sprints ----------
//
// Outside the draft on purpose. The draft exists because the settings PUT
// validates as a unit; a sprint is its own row with its own rules (one active
// at a time, closing moves work), so each action saves on its own and the
// server's answer is shown against it.

const SPRINT_TONE = { planned: "neutral", active: "accent", closed: "success" } as const

function SprintsCard({ projectId }: { projectId: string }) {
  const toast = useToast()
  const loaded = useLoad(() => api.get<{ sprints: Sprint[] }>(`/projects/${projectId}/sprints`), [projectId])
  const [error, setError] = useState<unknown>(null)
  const [closing, setClosing] = useState<Sprint | null>(null)
  const [name, setName] = useState("")
  const [start, setStart] = useState("")
  const [end, setEnd] = useState("")
  const [showClosed, setShowClosed] = useState(false)

  const sprints = loaded.data?.sprints ?? []
  const active = sprints.find((sprint) => sprint.status === "active")
  const visible = showClosed ? sprints : sprints.filter((sprint) => sprint.status !== "closed")
  const closedCount = sprints.length - sprints.filter((sprint) => sprint.status !== "closed").length

  async function act(run: () => Promise<unknown>, message?: string) {
    setError(null)
    try {
      await run()
      if (message) toast(message)
      loaded.reload()
    } catch (err) {
      setError(err)
    }
  }

  return (
    <Card className="flex flex-col gap-3 p-4">
      <div>
        <h2 className="text-sm font-semibold text-foreground">Sprints</h2>
        <p className="text-xs text-muted">
          A task sits in at most one sprint. One sprint is current at a time; closing it moves its
          unfinished tasks on. Changes here save as you make them.
        </p>
      </div>

      <ErrorNote error={error ?? loaded.error} />

      {loaded.loading && !sprints.length && <Skeleton className="h-10" />}
      {!loaded.loading && visible.length === 0 && <span className="text-xs text-faint">None yet.</span>}

      {visible.map((sprint) => (
        <RowShell key={sprint.id}>
          <SprintName
            sprint={sprint}
            onSave={(next) => act(() => api.patch(`/projects/sprints/${sprint.id}`, { name: next }), "Sprint renamed")}
          />
          <Badge tone={SPRINT_TONE[sprint.status]}>
            {sprint.status === "active" ? "Current" : sprint.status === "planned" ? "Planned" : "Closed"}
          </Badge>
          {sprint.status === "closed" ? (
            <span className="text-[11px] text-faint">
              {sprint.startDate ? formatDate(sprint.startDate) : "—"} → {sprint.endDate ? formatDate(sprint.endDate) : "—"}
            </span>
          ) : (
            <span className="flex items-center gap-1">
              <SprintDate
                label="Start date"
                value={sprint.startDate}
                onSave={(startDate) => act(() => api.patch(`/projects/sprints/${sprint.id}`, { startDate }))}
              />
              <span className="text-faint">→</span>
              <SprintDate
                label="End date"
                value={sprint.endDate}
                onSave={(endDate) => act(() => api.patch(`/projects/sprints/${sprint.id}`, { endDate }))}
              />
            </span>
          )}
          <span className="ml-auto flex items-center gap-2">
            {sprint.taskCount !== undefined && (
              <span className="text-[11px] text-faint">
                {sprint.taskCount} {sprint.taskCount === 1 ? "task" : "tasks"}
              </span>
            )}
            {sprint.status === "planned" && (
              <button
                type="button"
                className="text-xs text-faint hover:text-foreground disabled:cursor-not-allowed disabled:opacity-40"
                disabled={!!active}
                title={active ? `Close ${active.name} first` : "Make this the current sprint"}
                onClick={() => act(() => api.post(`/projects/sprints/${sprint.id}/start`), `${sprint.name} started`)}
              >
                start
              </button>
            )}
            {sprint.status !== "closed" && (
              <button
                type="button"
                className="text-xs text-faint hover:text-danger"
                onClick={() => setClosing(sprint)}
              >
                close
              </button>
            )}
          </span>
        </RowShell>
      ))}

      {closedCount > 0 && (
        <button
          type="button"
          className="self-start text-xs text-faint underline underline-offset-2 hover:text-foreground"
          onClick={() => setShowClosed((v) => !v)}
        >
          {showClosed ? "Hide closed sprints" : `Show ${closedCount} closed`}
        </button>
      )}

      <div className="flex flex-wrap items-end gap-2 border-t border-border pt-3">
        <Field label="New sprint" value={name} onChange={(e) => setName(e.target.value)} placeholder="Sprint 14" />
        <Field label="Start" type="date" value={start} onChange={(e) => setStart(e.target.value)} />
        <Field label="End" type="date" value={end} onChange={(e) => setEnd(e.target.value)} />
        <Button
          variant="subtle"
          disabled={!name.trim()}
          onClick={() =>
            act(async () => {
              await api.post(`/projects/${projectId}/sprints`, {
                name: name.trim(),
                startDate: fromDateInput(start),
                endDate: fromDateInput(end),
              })
              setName("")
              setStart("")
              setEnd("")
            }, "Sprint added")
          }
        >
          Add
        </Button>
      </div>

      {closing && (
        <CloseSprintModal
          sprint={closing}
          targets={sprints.filter((s) => s.status !== "closed" && s.id !== closing.id)}
          onClose={() => setClosing(null)}
          // Not through act(): a refusal belongs in the dialog the admin is
          // looking at, not on the card behind it.
          onConfirm={async (moveOpenTo) => {
            const result = await api.post<{ sprint: Sprint; moved: number }>(
              `/projects/sprints/${closing.id}/close`,
              { moveOpenTo },
            )
            setClosing(null)
            toast(
              result.moved
                ? `${closing.name} closed — ${result.moved} open ${result.moved === 1 ? "task" : "tasks"} moved`
                : `${closing.name} closed`,
            )
            loaded.reload()
          }}
        />
      )}
    </Card>
  )
}

/** Renames on blur or Enter, not per keystroke: each save is a request, and a
 *  half-typed name would briefly be the sprint's real one. */
function SprintName({ sprint, onSave }: { sprint: Sprint; onSave: (name: string) => void }) {
  const [value, setValue] = useState(sprint.name)
  useEffect(() => setValue(sprint.name), [sprint.name])
  const commit = () => {
    const next = value.trim()
    if (next && next !== sprint.name) onSave(next)
    else setValue(sprint.name)
  }
  return (
    <input
      value={value}
      aria-label="Sprint name"
      disabled={sprint.status === "closed"}
      onChange={(e) => setValue(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === "Enter") e.currentTarget.blur()
      }}
      className="w-40 rounded border border-transparent bg-transparent px-1.5 py-0.5 text-sm text-foreground outline-none hover:border-border focus:border-interactive disabled:hover:border-transparent"
    />
  )
}

function SprintDate({
  label,
  value,
  onSave,
}: {
  label: string
  value: string | null
  onSave: (value: string | null) => void
}) {
  return (
    <input
      type="date"
      aria-label={label}
      value={toDateInput(value)}
      onChange={(e) => onSave(fromDateInput(e.target.value))}
      className="rounded border border-border bg-surface px-1.5 py-0.5 text-xs text-foreground outline-none focus:border-interactive"
    />
  )
}

function CloseSprintModal({
  sprint,
  targets,
  onClose,
  onConfirm,
}: {
  sprint: Sprint
  targets: Sprint[]
  onClose: () => void
  onConfirm: (moveOpenTo: string | null) => Promise<void>
}) {
  // Defaults to the next planned sprint, as before; any other open sprint, or
  // none, is a choice away.
  const [target, setTarget] = useState<string>(targets.find((s) => s.status === "planned")?.id ?? "")
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<unknown>(null)

  return (
    <Modal
      title={`Close ${sprint.name}`}
      onClose={onClose}
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="subtle" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant="danger"
            disabled={busy}
            onClick={async () => {
              setBusy(true)
              setError(null)
              try {
                await onConfirm(target || null)
              } catch (err) {
                setError(err)
              } finally {
                setBusy(false)
              }
            }}
          >
            {busy ? "Closing…" : "Close sprint"}
          </Button>
        </div>
      }
    >
      <p className="text-sm text-muted">
        Finished tasks stay in {sprint.name}. Unfinished tasks move to the sprint you pick below.
      </p>
      <Select label="Move unfinished tasks to" value={target} onChange={(e) => setTarget(e.target.value)}>
        <option value="">No sprint</option>
        {targets.map((s) => (
          <option key={s.id} value={s.id}>
            {s.name}
            {s.status === "active" ? " · current" : ""}
          </option>
        ))}
      </Select>
      {!targets.length && <p className="text-xs text-faint">There is no other open sprint to move them to.</p>}
      <ErrorNote error={error} />
    </Modal>
  )
}
